import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import * as trainersApi from '../../api/trainers'
import { useUIStore } from '../../stores/uiStore'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'

/**
 * Trainer Create Page
 * Form to create a new trainer profile
 */
export default function TrainerCreate() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useUIStore()
  
  const [formData, setFormData] = useState({
    uid: '',
    first_name: '',
    last_name: '',
    email: '',
    iban: '',
    trainer_tennaamstelling: '',
  })

  const createMutation = useMutation({
    mutationFn: (data) => trainersApi.createTrainer(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['trainers'])
      queryClient.invalidateQueries(['dashboard-stats'])
      showToast('Trainer created successfully!', 'success')
      navigate('/trainers')
    },
    onError: (error) => {
      showToast(error.message || 'Failed to create trainer', 'error')
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    createMutation.mutate(formData)
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Create Trainer</h1>
        <p className="mt-2 text-gray-600">Fill in the details to create a new trainer profile</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <Card.Header>
            <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  placeholder="John"
                />

                <Input
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  placeholder="Doe"
                />
              </div>

              <Input
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="john.doe@example.com"
              />

              <Input
                label="IBAN (Optional)"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                placeholder="NL91ABNA0417164300"
              />

              <Input
                label="Tennaamstelling (Optional)"
                name="trainer_tennaamstelling"
                value={formData.trainer_tennaamstelling}
                onChange={handleChange}
                placeholder="e.g., T.n.v. John Doe"
                helpText="Name to which payments should be addressed"
              />

              <Input
                label="UID (Optional)"
                name="uid"
                value={formData.uid}
                onChange={handleChange}
                placeholder="Unique identifier"
              />
            </div>
          </Card.Body>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/trainers')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={createMutation.isPending}
          >
            Create Trainer
          </Button>
        </div>
      </form>
    </div>
  )
}